import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Field,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ApiError } from '@/lib/api/errors';
import type { UserId } from '@/lib/api/ids';
import type { OrgKind, User, UserRole } from '@/lib/api/domains/users';
import { roleOptionsForOrgKind } from '../role-options';
import {
  NO_SET_VALUE,
  permissionSetOptionsForOrgKind,
} from '../permission-set-options';
import { PermissionSetChooser } from './PermissionSetChooser';
import { userQuery } from '../api/user-queries';
import {
  useUpdateUser,
  useDeactivateUser,
  useReactivateUser,
} from '../api/user-mutations';

/*
 * The user editor dialog (F02 R21/R22/R23), task 7.2. It opens an account
 * against WHAT IS CURRENTLY IN FORCE, lets an Administrator change it, deactivate
 * it behind a named confirmation, and reactivate a deactivated one by name:
 *
 *   - AC-cd9143ad (tpa half) — the editor OPENS AGAINST WHAT IS CURRENTLY IN
 *     FORCE: it reads the account through `userQuery(entityId, userId)`
 *     (staleTime 0) and binds the form to the server's CURRENT name, email,
 *     role, administrator setting, active setting and permission set — never a
 *     stale row cached off the list or form defaults. The read is `enabled`
 *     only while the dialog is open, so reopening re-reads and a change made
 *     elsewhere while it was closed shows the moment it is reopened. The form
 *     body is keyed on the loaded account's current values (see UserEditorForm),
 *     so a re-read with changes rebuilds the form against what is NOW in force.
 *     (The Identity half — `GET /users/:id` returns current state — is consumed
 *     through the mocked boundary.)
 *   - AC-ce795f57 — BEFORE deactivating, the editor shows a confirmation that
 *     NAMES THE PERSON and STATES WHAT DEACTIVATION DOES; the deactivate never
 *     fires without that confirm (composed ConfirmDialog, destructive).
 *   - AC-74de7113 — deactivation is OFFERED ONLY ON AN ACTIVE account: when the
 *     account in force is already inactive, no deactivate affordance is shown
 *     (the reactivation affordance is shown instead).
 *   - AC-25350d0f — bringing a deactivated account back is NAMED REACTIVATION:
 *     the affordance is labelled "Reactivate", distinct from the create/edit
 *     Save, so the act is recognisable as reactivation and not merely an edit.
 *   - AC-fa0ba0de (tpa half) — on a successful reactivation the editor re-reads
 *     (mutate → invalidate) and the permission set the account held COMES BACK,
 *     without anyone reassigning it. The set-restore is Identity's (CR-E01-10);
 *     the editor only invokes the reactivate seam and reads the restored set
 *     back off the live re-read.
 *
 * Mutate → invalidate, no optimistic update (every write, like the add path):
 * `useUpdateUser` for an edit, `useDeactivateUser` on confirm, `useReactivateUser`
 * on the named affordance. A server refusal (the role↔org-kind pin, a failed
 * compound write) is surfaced IN-DIALOG like AddUserDialog, never swallowed, so
 * nothing shows as saved when the server refused (AC-76f5f026).
 *
 * The deactivate/reactivate requests carry the account id ONLY — the person's
 * name is shown in the UI (and the confirm), never sent (PII stays out of
 * requests/URLs/telemetry).
 *
 * Seams left for later tasks: the permission-set chooser inside this editor is
 * task 8 (AC-b31e4456); the set field is read and preserved here but not yet
 * editable. The last-login display is task 9. The server is the authority on
 * who may edit (task 1's gate); this surface is the courtesy half.
 *
 * DEPENDS re-check (BUILT ON ASSUMPTIONS):
 *   - CR-E01-07 (confirm): the seven-year retention hold binds the Identity
 *     row. The editor's deactivate is a soft deactivate that KEEPS the record;
 *     the retention floor is asserted on the domain/record side (domain-svc).
 *     If CR-E01-07 is declined, re-check that deactivation here never deletes.
 *   - CR-E01-10 (confirm): reactivation restores the prior set. The editor
 *     reads the restored set back from the re-read; the restore itself is the
 *     mocked boundary's. If declined, re-check the restored-set assertion.
 */
export function UserEditorDialog({
  open,
  onOpenChange,
  entityId,
  userId,
  orgKind,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The organization the account belongs to. */
  entityId: string;
  /** The account being edited, read against current state. */
  userId: UserId;
  /** The organization's kind (decides which roles the editor offers). */
  orgKind: OrgKind;
}): React.ReactElement | null {
  // Open against WHAT IS IN FORCE (AC-cd9143ad): read the account live. The read
  // is enabled only while the dialog is open, so reopening re-reads and a
  // change made elsewhere while it was closed shows on reopen.
  const { data: account } = useQuery({
    ...userQuery(entityId, userId),
    enabled: open,
  });

  if (!open) return null;

  // The dialog mounts only once the CURRENT state is in hand, so it opens bound
  // to what is in force — never a stale row or empty defaults (AC-cd9143ad). A
  // stale snapshot is never shown; while the live read is in flight the editor
  // renders nothing, and the dialog (with the account's current values) appears
  // the moment the read resolves. Because the editor unmounts while closed,
  // reopening re-reads and rebinds the form against what is NOW in force.
  if (!account) return null;

  return (
    <UserEditorForm
      open={open}
      onOpenChange={onOpenChange}
      entityId={entityId}
      userId={userId}
      orgKind={orgKind}
      account={account}
    />
  );
}

/*
 * The form body, built from the loaded account. Separated so `useForm`'s initial
 * values ARE the current state: the parent renders this only once the account
 * has loaded (and unmounts it while closed), so the form always initializes
 * against what is in force. The account prop stays live, so a value that changes
 * after mount — the active state after a deactivate/reactivate — drives the UI
 * (the deactivate affordance gives way to the named reactivation one) WITHOUT
 * remounting the dialog, keeping the dialog node stable through the cycle.
 */
function UserEditorForm({
  open,
  onOpenChange,
  entityId,
  userId,
  orgKind,
  account,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  userId: UserId;
  orgKind: OrgKind;
  account: User;
}): React.ReactElement {
  const roleOptions = roleOptionsForOrgKind(orgKind);
  // The editor's set chooser offers the "none" option plus every non-baseline
  // set this org kind may hold, single-select; choosing one REPLACES the set
  // held (AC-ef201ead), never accumulating (AC-463fbe56).
  const setOptions = permissionSetOptionsForOrgKind(orgKind);

  const updateUser = useUpdateUser(entityId, userId);
  const deactivateUser = useDeactivateUser(entityId);
  const reactivateUser = useReactivateUser(entityId);

  // The deactivate confirmation (AC-ce795f57) is transient local state, not a
  // search param (ConfirmDialog's contract).
  const [confirmOpen, setConfirmOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      displayName: account.displayName,
      email: account.email,
      role: account.role as UserRole | null,
      isAdministrator: account.isAdministrator,
      // The chooser opens on the ONE set the account currently holds
      // (AC-cd9143ad open against current state; AC-463fbe56 at-most-one). A
      // null set (baseline alone) maps to the "none" sentinel so the chooser
      // selects "none".
      permissionSet: account.permissionSet ?? NO_SET_VALUE,
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await updateUser.mutateAsync({
            displayName: value.displayName,
            email: value.email,
            role: value.role as UserRole,
            isAdministrator: value.isAdministrator,
            // Send a SINGLE scalar set id (replace, not add — AC-ef201ead), or
            // null for the baseline alone when "none" is chosen (AC-de110ec1).
            // Never an array.
            permissionSet:
              value.permissionSet === NO_SET_VALUE ? null : value.permissionSet,
          });
          return null;
        } catch (error) {
          // Surface the server's refusal where the operator works, exactly like
          // the add path: field-shaped refusals map back onto the fields;
          // anything else becomes a form-level message. Either way the save is
          // treated as a failure (onSubmit never runs), so nothing shows as
          // saved when the server refused (AC-76f5f026).
          // A 412 (precondition failed) is the concurrent-save collision
          // (finding 8; the "second save is refused" row): the account changed
          // since this editor opened it. Surface the collision where the
          // operator works and keep the dialog open — nothing is shown as
          // saved. Named distinctly from a generic failure so the operator
          // knows to re-open and re-apply.
          if (error instanceof ApiError && error.status === 412) {
            return {
              form: 'This account was changed by someone else since you opened it. Reopen it to see the current values and try again.',
            };
          }
          if (error instanceof ApiError && error.fieldErrors?.length) {
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) fields[fe.field] = fe.message;
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'That change could not be saved.',
          };
        }
      },
    },
    onSubmit: () => {
      onOpenChange(false);
    },
  });

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Edit user"
        onSubmit={() => void form.handleSubmit()}
        submitLabel="Save"
        pending={updateUser.isPending}
      >
        {/* The server's refusal, surfaced where the operator works. The refusal
            text is the server's; we render it, never composing our own
            cause-specific wording (one generic refusal regardless of cause). */}
        <form.Subscribe selector={(state) => state.errorMap.onSubmit}>
          {(formError) =>
            formError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  {typeof formError === 'string'
                    ? formError
                    : String(
                        (formError as { form?: unknown })?.form ?? formError,
                      )}
                </AlertDescription>
              </Alert>
            ) : null
          }
        </form.Subscribe>

        <form.Field name="displayName">
          {(field) => (
            <FormField
              label="Name"
              errors={fieldErrorMessages(field.state.meta.errors)}
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              )}
            </FormField>
          )}
        </form.Field>

        <form.Field name="email">
          {(field) => (
            <FormField
              label="Email"
              errors={fieldErrorMessages(field.state.meta.errors)}
            >
              {({ id }) => (
                <Input
                  id={id}
                  type="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              )}
            </FormField>
          )}
        </form.Field>

        <form.Field name="role">
          {(field) => (
            <FieldSet>
              <FieldLegend variant="label">Role</FieldLegend>
              {/* The chooser offers only the org kind's roles (AC-23bf376f);
                  the server pins the role by org kind on save. */}
              <RadioGroup
                value={field.state.value ?? null}
                onValueChange={(next) =>
                  field.handleChange(next as UserRole | null)
                }
                aria-label="Role"
              >
                {roleOptions.map((option) => (
                  <FieldLabel key={option.value}>
                    <Field orientation="horizontal">
                      <RadioGroupItem value={option.value} />
                      <span>{option.label}</span>
                    </Field>
                  </FieldLabel>
                ))}
              </RadioGroup>
            </FieldSet>
          )}
        </form.Field>

        {/* The permission-set chooser (R24): single-select, opened on the one
            set the account currently holds (AC-cd9143ad / AC-463fbe56). Choosing
            a different set REPLACES the one held (AC-ef201ead); choosing "none"
            clears it to the baseline alone (AC-de110ec1). The save carries one
            scalar set id (or null), never an array. */}
        <form.Field name="permissionSet">
          {(field) => (
            <PermissionSetChooser
              name="edit-user-permission-set"
              options={setOptions}
              value={field.state.value ?? NO_SET_VALUE}
              onValueChange={(next) => field.handleChange(next)}
            />
          )}
        </form.Field>

        <form.Field name="isAdministrator">
          {(field) => (
            <Field orientation="horizontal">
              <Checkbox
                checked={field.state.value}
                onCheckedChange={(checked) =>
                  field.handleChange(checked === true)
                }
                id="edit-user-administrator"
              />
              <FieldLabel htmlFor="edit-user-administrator">
                Administrator privilege
              </FieldLabel>
            </Field>
          )}
        </form.Field>

        {/* Deactivate / reactivate affordances, mutually exclusive on the
            account's current active state. Deactivation is offered ONLY on an
            active account (AC-74de7113) and goes through a named confirmation
            (AC-ce795f57); bringing a deactivated account back is NAMED
            reactivation (AC-25350d0f), distinct from this Save. */}
        <Field orientation="horizontal">
          {account.isActive ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              disabled={reactivateUser.isPending}
              onClick={() => {
                // Bring the account back by reusing the existing row; the set it
                // held is restored by Identity (CR-E01-10). Targets the account
                // by id only — the name is UI-only, never sent.
                reactivateUser.mutate(userId);
              }}
            >
              {reactivateUser.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          )}
        </Field>

        {/* A refused reactivation is surfaced where the operator works and the
            editor stays open so it can be retried — "nothing fails silently"
            (finding 11), symmetric with the save path's in-dialog refusal. One
            generic text; the 401/403 interceptor is the backstop. */}
        {reactivateUser.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              That account could not be reactivated. Please try again.
            </AlertDescription>
          </Alert>
        ) : null}
      </FormDialog>

      {/* The deactivation confirmation (AC-ce795f57): it NAMES THE PERSON and
          STATES WHAT DEACTIVATION DOES, before anything happens. The deactivate
          fires only on accept, targeting the account by id (the name is shown
          here, never sent). Destructive styling. */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Deactivate user"
        description={
          <span className="flex flex-col gap-2">
            <span>{`Deactivate ${account.displayName}? They will no longer be able to sign in or access this organization. The account is kept and can be reactivated later.`}</span>
            {/* A refused deactivate is surfaced in the confirm and it stays
                open to retry — "nothing fails silently" (finding 11). */}
            {deactivateUser.isError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  That account could not be deactivated. Please try again.
                </AlertDescription>
              </Alert>
            ) : null}
          </span>
        }
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        destructive
        onConfirm={async () => {
          // mutateAsync rejects on failure, so ConfirmDialog keeps the dialog
          // open and the failure renders above; it closes only on success.
          await deactivateUser.mutateAsync(userId);
        }}
      />
    </>
  );
}
